import { useCallback, useEffect, useMemo, useState } from "react";
import { CatalogImage } from "./CatalogImage";
import { moneyFormatter } from "../lib/money";
import type { CustomerOrder, OrderLine, OrderStatus } from "../types/catalog";
import "./AddProductModal.css";
import "./CartPanel.css";
import "./AdminFulfillmentPanel.css";

type AdminFulfillmentPanelProps = {
  orderId: string;
  onClose: () => void;
  onReleaseClaim: (orderId: string) => Promise<{ message: string; ok: boolean }>;
  onOrderStatusChange: (
    orderId: string,
    status: OrderStatus
  ) => Promise<{ message: string; ok: boolean }>;
  onRefreshOrders: () => Promise<void>;
};

type OrderResponse = {
  order: CustomerOrder;
};

type VerificationResponse = {
  amountKes?: number;
  error?: string;
  method?: string;
  names?: {
    first: string;
    last: string;
    middle: string;
  };
  orderCode?: string;
  orderId?: string;
  paymentStatus?: string;
  transactionId?: string;
};

function readableStatus(status: string) {
  return status.replaceAll("_", " ");
}

function packedCount(items: OrderLine[]) {
  return items.reduce((total, item) => total + Number(item.packedQty ?? 0), 0);
}

function itemCount(items: OrderLine[]) {
  return items.reduce((total, item) => total + item.qty, 0);
}

function allPacked(items: OrderLine[]) {
  return items.length > 0 && items.every((item) => Number(item.packedQty ?? 0) >= item.qty);
}

function statusOptions(order: CustomerOrder) {
  const options: OrderStatus[] = [
    "packing",
    "cancellation_requested",
    order.fulfillmentMethod === "pickup" ? "ready_for_pickup" : "out_for_delivery",
    "completed",
    "cancelled",
  ];

  return Array.from(new Set([order.status, ...options]));
}

export function AdminFulfillmentPanel({
  orderId,
  onClose,
  onReleaseClaim,
  onOrderStatusChange,
  onRefreshOrders,
}: AdminFulfillmentPanelProps) {
  const [order, setOrder] = useState<CustomerOrder | null>(null);
  const [status, setStatus] = useState("");
  const [statusDraft, setStatusDraft] = useState<OrderStatus>("packing");
  const [statusTone, setStatusTone] = useState<"error" | "neutral" | "success">("neutral");
  const [verification, setVerification] = useState<VerificationResponse | null>(null);

  const refreshOrder = useCallback(async () => {
    setStatus("Loading fulfillment...");

    const response = await fetch(`/api/orders/${encodeURIComponent(orderId)}`);

    if (!response.ok) {
      setStatus("Order could not be loaded.");
      return;
    }

    const body = await response.json<OrderResponse>();
    setOrder(body.order);
    setStatusDraft(body.order.status);
    setStatus("");
    setStatusTone("neutral");
  }, [orderId]);

  useEffect(() => {
    setOrder(null);
    setVerification(null);
    setStatus("");
    setStatusTone("neutral");
    void refreshOrder();
  }, [refreshOrder]);

  const packedSummary = useMemo(() => {
    if (!order) {
      return { allPacked: false, packed: 0, total: 0 };
    }

    return {
      allPacked: allPacked(order.items),
      packed: packedCount(order.items),
      total: itemCount(order.items),
    };
  }, [order]);

  async function updatePackedQty(item: OrderLine, packedQty: number) {
    if (!order) {
      return;
    }

    setStatus("Updating packed item...");
    setStatusTone("neutral");

    const response = await fetch(`/api/orders/${encodeURIComponent(order.id)}/items`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        itemId: item.id,
        packedQty,
      }),
    });

    if (!response.ok) {
      const body = await response.json<{ error?: string }>();
      setStatus(body.error ?? "Packed quantity was not updated.");
      setStatusTone("error");
      return;
    }

    await refreshOrder();
    await onRefreshOrders();
    setStatus("Packed item updated.");
    setStatusTone("success");
  }

  async function verifyPayment() {
    if (!order) {
      return;
    }

    setStatus("Verifying payment...");
    setStatusTone("neutral");

    const response = await fetch(`/api/orders/${encodeURIComponent(order.id)}/verify-payment`, {
      method: "POST",
    });
    const body = await response.json<VerificationResponse>();

    setVerification(body);
    setStatus(response.ok ? "Payment verified." : body.error ?? "Payment could not be confirmed.");
    setStatusTone(response.ok ? "success" : "error");

    if (response.ok) {
      await refreshOrder();
      await onRefreshOrders();
    }
  }

  async function applyStatus(nextStatus: OrderStatus) {
    if (!order) {
      return;
    }

    setStatus("Updating order...");
    setStatusTone("neutral");

    const result = await onOrderStatusChange(order.id, nextStatus);
    await refreshOrder();
    await onRefreshOrders();
    setStatus(result.message);
    setStatusTone(result.ok ? "success" : "error");
  }

  async function releaseClaim() {
    if (!order) {
      return;
    }

    setStatus("Releasing order...");
    setStatusTone("neutral");
    const result = await onReleaseClaim(order.id);
    await onRefreshOrders();
    setStatus(result.message);
    setStatusTone(result.ok ? "success" : "error");

    if (result.ok) {
      onClose();
    }
  }

  if (!order) {
    return (
      <div className="modal-backdrop admin-fulfillment-backdrop" role="presentation">
        <section
          aria-live="polite"
          aria-modal="true"
          className="modal bottom-sheet admin-fulfillment-modal"
          role="dialog"
        >
          <div className="modal__handle" />
          <header className="modal__header">
            <div>
              <p className="eyebrow">Fulfillment</p>
              <h2>{status || "Loading fulfillment"}</h2>
            </div>
            <button
              className="button button--ghost"
              type="button"
              onClick={(event) => {
                event.currentTarget.blur();
                onClose();
              }}
            >
              Close
            </button>
          </header>
        </section>
      </div>
    );
  }

  return (
    <div className="modal-backdrop admin-fulfillment-backdrop" role="presentation">
      <section
        aria-labelledby="fulfillment-title"
        aria-modal="true"
        className="modal bottom-sheet admin-fulfillment-modal"
        role="dialog"
      >
        <div className="modal__handle" />
        <header className="modal__header">
          <div>
            <p className="eyebrow">Fulfillment {order.orderCode}</p>
            <h2 id="fulfillment-title">{order.customerName}</h2>
            <p className="modal__hint">
              {order.phone} - packed {packedSummary.packed}/{packedSummary.total}
            </p>
          </div>
          <button
            className="button button--ghost"
            type="button"
            onClick={(event) => {
              event.currentTarget.blur();
              onClose();
            }}
          >
            Close
          </button>
        </header>

        <div className="modal__forms admin-fulfillment-modal__body">
          <div className="admin-fulfillment__items">
            {order.items.map((item) => {
              const packedQty = Number(item.packedQty ?? 0);
              const isPacked = packedQty >= item.qty;

              return (
                <article className="cart-line admin-pack-line" key={item.id}>
                  <CatalogImage
                    className="cart-line__image"
                    alt={item.imageAlt}
                    preset="thumb"
                    storageKey={item.imageStorageKey ?? ""}
                  />
                  <div className="cart-line__content">
                    <p className="eyebrow">{item.category || item.sku}</p>
                    <h3>{item.name}</h3>
                    <p>{item.sku}</p>
                    <dl className="cart-line__meta">
                      <div>
                        <dt>Qty</dt>
                        <dd>{item.qty}</dd>
                      </div>
                      <div>
                        <dt>Packed</dt>
                        <dd>
                          {packedQty}/{item.qty}
                        </dd>
                      </div>
                      <div>
                        <dt>Unit price</dt>
                        <dd>{moneyFormatter.format(item.unitPriceKes)}</dd>
                      </div>
                      <div className="cart-line__meta-total">
                        <dt>Line total</dt>
                        <dd>{moneyFormatter.format(item.qty * item.unitPriceKes)}</dd>
                      </div>
                    </dl>
                  </div>
                  <div className="cart-line__controls admin-pack-line__controls">
                    <div className="cart-line__quantity" aria-label={`Packed quantity for ${item.name}`}>
                      <span>Packed</span>
                      <div className="cart-line__stepper">
                        <button
                          aria-label={`Reduce packed quantity for ${item.name}`}
                          disabled={packedQty <= 0}
                          type="button"
                          onClick={(event) => {
                            event.currentTarget.blur();
                            void updatePackedQty(item, Math.max(0, packedQty - 1));
                          }}
                        >
                          -
                        </button>
                        <output>{packedQty}</output>
                        <button
                          aria-label={`Increase packed quantity for ${item.name}`}
                          disabled={packedQty >= item.qty}
                          type="button"
                          onClick={(event) => {
                            event.currentTarget.blur();
                            void updatePackedQty(item, Math.min(item.qty, packedQty + 1));
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="admin-pack-line__buttons">
                      <button
                        aria-pressed={isPacked}
                        className={`button ${
                          isPacked ? "button--ghost admin-pack-line__toggle--packed" : "button--secondary"
                        }`}
                        type="button"
                        onClick={(event) => {
                          event.currentTarget.blur();
                          void updatePackedQty(item, isPacked ? 0 : item.qty);
                        }}
                      >
                        {isPacked ? "Unmark packed" : "Mark packed"}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <footer className="modal__footer admin-fulfillment-modal__footer">
            {status ? (
              <p
                className={`modal__status admin-fulfillment-modal__status modal__status--${statusTone}`}
                role={statusTone === "error" ? "alert" : "status"}
              >
                {status}
              </p>
            ) : null}
            <div className="admin-fulfillment__payment-actions">
              <button
                className="button"
                disabled={!packedSummary.allPacked}
                type="button"
                onClick={(event) => {
                  event.currentTarget.blur();
                  void verifyPayment();
                }}
              >
                Verify payment
              </button>
            </div>
            {verification ? (
              <p className="modal__status admin-fulfillment-modal__status">
                {verification.transactionId
                  ? `Payment ${verification.transactionId}`
                  : verification.paymentStatus ?? verification.error}
              </p>
            ) : null}
            <label className="admin-fulfillment__status-field">
              Order status
              <select
                value={statusDraft}
                onChange={(event) => setStatusDraft(event.target.value as OrderStatus)}
              >
                {statusOptions(order).map((option) => (
                  <option key={option} value={option}>
                    {readableStatus(option)}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="button"
              type="button"
              onClick={(event) => {
                event.currentTarget.blur();
                void applyStatus(statusDraft);
              }}
            >
              Apply status
            </button>
            <button
              className="button button--secondary"
              type="button"
              onClick={(event) => {
                event.currentTarget.blur();
                void releaseClaim();
              }}
            >
              Release claim
            </button>
            <button
              className="button button--ghost"
              type="button"
              onClick={(event) => {
                event.currentTarget.blur();
                onClose();
              }}
            >
              Done
            </button>
          </footer>
        </div>
      </section>
    </div>
  );
}
